import React, { useLayoutEffect, useRef } from 'react';
import HtmlParser from 'react-html-parser';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { guestListData } from '@data-ts/pages/guest-list';
import { TextWrapper, Lottie, LottieContainer, ContentWrapper, SectionCloudImage, StyledPhotoCredit } from './TagTheyAreIt.styles';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { PhotoCredit } from '@components/PhotoCredit';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';

export const WeddingParty: React.FC<{}> = () => {
  const weddingContainerRef = useRef<HTMLDivElement>(null);
  const weddingRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const { animationRef, loadAnimation } = useLottieAnimation(
    {
      path: cloudAssetUrl('sitewide/block_emotional-break/guestlist2/bodymovin-bounce/data.json'), // Required
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'wedding party hero',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: weddingRef }
  );

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let weddingPartyHeroTimeline: ReturnType<typeof gsap.timeline>;

    const weddingContainerEl = weddingContainerRef.current;

    if (scrollController && isMounted && weddingContainerEl) {
      weddingPartyHeroTimeline = gsap.timeline({ paused: true });
      weddingPartyHeroTimeline.to({ frame: 0 }, 1, {
        onUpdate: () => {
          if (animationRef.current) {
            animationRef.current?.goToAndStop(
              Math.round(weddingPartyHeroTimeline.totalProgress() * animationRef.current.totalFrames),
              true
            );
          }
        },
        ease: Linear.easeNone,
      });

      scene = new ScrollMagic.Scene({
        triggerElement: weddingContainerEl,
        triggerHook: 'onEnter',
        duration: '400%',
      })
        .addTo(scrollController)
        .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          weddingPartyHeroTimeline?.progress(event.progress);
        });
    }
    const clearScene = () => {
      if (weddingPartyHeroTimeline) {
        weddingPartyHeroTimeline.progress(0);
        weddingPartyHeroTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted, animationRef]);

  return (
    <LottieContainer ref={weddingContainerRef} id="WeddingPartyContainer">
      <LazyLoadWrapper beforeLoad={loadAnimation}>
        <div />
      </LazyLoadWrapper>
      <Lottie ref={weddingRef}>
        <StyledPhotoCredit color="red3" leftIconVariant offset="bottom-left" id="photoCreditGuestListWeddingParty" />
      </Lottie>
      <SectionCloudImage src="sitewide/block_emotional-break/guestlist2/weddingparty_bg.jpg" alt="wedding-party" />
      <ContentWrapper>
        <TextWrapper color="yellow1" variant="hed96Break" tagName="h2">
          {HtmlParser(guestListData.celebrateYourLove.weddingPartTitle)}
        </TextWrapper>
      </ContentWrapper>
    </LottieContainer>
  );
};
